import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowDown, ArrowUp, Drone, Moon, Play } from "lucide-react";
import { useState } from "react";

import { api } from "../../api/client";
import type {
  CoaCandidate,
  CoaOrientation,
  CoaRole,
  CoaScenario,
  EnemyCoa,
  Phase,
  SmallDrones,
  WargameSimulation,
} from "../../api/types";
import { EmptyState } from "../../components/EmptyState";
import { Motion, Presence } from "../../components/presence";
import { SaveDossierHint } from "../../components/SaveDossierHint";
import { Badge, Button, Chip, SectionLabel, Segmented } from "../../components/ui";
import { ViewHeader } from "../../components/ViewHeader";
import { smallDronesEffect } from "../../drones";
import { genericEnemyLabel, isNeutral } from "../../enemyDisplay";
import { citedUnit } from "../../i18n/designation";
import { k, t, tc, tServer } from "../../i18n/i18n";
import { missionName, term } from "../../i18n/vocabulary";
import { effectiveOpo } from "../../map/units/useDeployment";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { darkNightText, num, seasonalLabel, SKY_LABELS, skyLabel, WIND_FROM_SKY, windLabel } from "../../weather";
import { pictogramIcon } from "../../weatherIcons";
import { DossierEmptyState } from "../DossierEmptyState";
import { WEATHER_FIELD } from "../missionDataTarget";
import { showMissionField } from "../openMissionData";
import { OpoSelector } from "../order/OrderView";
import { SourcesNote } from "../sketch/SketchBase";
import { CoaSketch, ROLE_DASH, type SketchPhase } from "./CoaSketch";
import { WargamePanel } from "./WargamePanel";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitState } from "../../map/zones/ZoneWaitCallout";

/** Modes d'action ennemis (vue « Modes d'action ») : ME1, le plus probable, et ME2, le plus dangereux, tels
 *  que l'OPO les retient (§1.a iv), chacun avec son croquis — zones des échelons ennemis à chaque temps le long
 *  du fuseau, ou leurs trajectoires de T1 à T3 — et la trace du choix. La popup « Modes d'Action ENI » de
 *  l'iPad, sans ses défauts (registre D14). */

const PHASE_OPTIONS: { value: SketchPhase; label: string }[] = [
  { value: "T1", label: "T1" },
  { value: "T2", label: "T2" },
  { value: "T3", label: "T3" },
  { value: "all", label: k("Tous") },
];
const PHASES: Phase[] = ["T1", "T2", "T3"];

const ORIENTATION: Record<CoaOrientation, string> = { offensive: k("Offensif"), defensive: k("Défensif"), mixed: k("Mixte") };
const TERRAIN: Record<string, string> = {
  urban: k("urbain"),
  forest: k("boisé"),
  open: k("ouvert"),
  mountain: k("montagneux"),
  corridor: k("en couloir"),
  mixed: k("mixte"),
  unknown: k("inconnu"),
};

const pct = (value: number | null) => (value === null ? null : Math.round(Math.min(1, Math.max(0, value)) * 100));

/** Météo APPLIQUÉE de l'exercice et son effet réel sur le classement (`coa._weather_detail` du serveur) : libellé
 *  (null : météo de saison), pictogramme, appréciation dans la langue lue, vol, poids (0 : sans effet), cause du
 *  mauvais temps et nuit sombre du jour de l'heure H. */
export type CoaWeather = Omit<NonNullable<EnemyCoa["context"]["weather_detail"]>, "label"> & {
  label: string | null;
  /** Météo choisie appliquée (ciel et vent) ; scénario du catalogue (dossier importé) ; vent régional de la carte. */
  choice?: { sky: string; wind: string | null } | null;
  scenario?: string | null;
  regional_wind?: string | null;
  pictogram?: string;
  severity?: number;
  ceiling_ft?: number | null;
  flight?: "ok" | "limited" | "suspended";
  weight?: number;
  cause?: string;
  dark?: boolean;
  dark_reason?: string;
  moon_pct?: number | null;
  /** État des petits drones de l'exercice, nuit sombre comprise (chantier « Drones ») ; absent d'un serveur
   *  antérieur. */
  small_drones?: SmallDrones | null;
};
/** Modulation d'un scénario par la météo (+1 servi, −1 desservi) et par la nuit sombre. */
type CoaMarks = { weather?: number; night?: boolean };
const marksOf = (c: CoaCandidate) => c as CoaCandidate & CoaMarks;

/** Effet de la météo sur le classement, selon sa cause (`me.weather_cause` du serveur). */
const WEATHER_EFFECT: Record<string, string> = {
  sight: k("Mauvaise visibilité : elle favorise l'infiltration, l'embuscade et le harcèlement, et défavorise les drones, l'appui aérien et les mouvements mécanisés dans la profondeur."),
  ceiling: k("Plafond bas : il favorise les actions qui échappent à notre observation aérienne et défavorise celles qui reposent sur les drones et l'appui aérien."),
  ground: k("Sol et précipitations : ils favorisent l'embuscade et le harcèlement, et défavorisent les mouvements mécanisés."),
  wind: k("Vent fort : il favorise les actions qui échappent à nos drones et hélicoptères, et défavorise celles qui reposent sur les drones ennemis."),
  gale: k("Vent de tempête : drones et hélicoptères cloués au sol, mouvements ralentis ; il favorise l'embuscade et le harcèlement."),
  temperature: k("Chaleur ou froid extrêmes : ils favorisent le harcèlement et défavorisent les mouvements mécanisés."),
};

/** Vent d'une météo choisie, après le ciel (« Brouillard, vent léger ») ; le vent régional par son nom. */
const WIND_QUALIFIERS: Record<string, string> = {
  faible: k("vent léger"),
  modere: k("vent modéré"),
  fort: k("vent fort"),
  tempete: k("vent de tempête"),
};

/** Nom de la météo appliquée, dans le vocabulaire du réglage Météo : ciel (et vent) d'une météo choisie, scénario
 *  du catalogue, sinon la météo de saison. */
export function weatherName(weather: CoaWeather): string {
  const choice = weather.choice;
  if (choice) {
    const sky = skyLabel(choice.sky);
    const wind = choice.wind ?? WIND_FROM_SKY;
    if (wind === WIND_FROM_SKY) return sky;
    const qualifier = wind === "regional" ? windLabel("regional", weather.regional_wind).toLowerCase() : t(WIND_QUALIFIERS[wind] ?? wind);
    return `${sky}, ${qualifier}`;
  }
  if (weather.scenario && weather.scenario in SKY_LABELS) return skyLabel(weather.scenario);
  return weather.label ?? seasonalLabel();
}

/** Ouvre les Données initiales sur le réglage Météo de l'exercice et le désigne. */
function openWeatherSetting() {
  showMissionField(WEATHER_FIELD);
}

export function WeatherChip({ weather }: { weather: CoaWeather }) {
  const [lo, hi] = weather.temp;
  const [near, far] = weather.visibility_km;
  const sky = weatherName(weather);
  const assessment = weather.assessment;
  const visibility = near === far ? t("visibilité {v} km", { v: num(near) }) : t("visibilité {lo} à {hi} km", { lo: num(near), hi: num(far) });
  // Infobulle : la ligne de l'aperçu du réglage Météo (ciel, températures et visibilité appliquées, appréciation) ;
  // puce : la visibilité quand elle est en cause, sinon les températures.
  const line = t("{sky}, {lo} à {hi} °C, {visibility} — {assessment}", { sky, lo, hi, visibility, assessment });
  const short = weather.low_visibility
    ? t("{sky}, {visibility} — {assessment}", { sky, visibility, assessment })
    : t("{sky}, {lo} à {hi} °C — {assessment}", { sky, lo, hi, assessment });
  const effect = (weather.weight ?? 0) > 0
    ? t(WEATHER_EFFECT[weather.cause ?? ""] ?? k("Mauvais temps : il module le classement des scénarios."))
    : t("Temps clément : sans effet sur le classement des scénarios.");
  return (
    <Chip icon={pictogramIcon(weather.pictogram)} onClick={openWeatherSetting}
      title={`${line}. ${t("Météo appliquée aux ordres.")} ${effect} ${t("Cliquer pour régler la météo de l'exercice.")}`}>
      {short}
    </Chip>
  );
}

/** Puce des petits drones (chantier « Drones ») quand la météo les limite ou les interdit, à côté de la puce météo ;
 *  son infobulle donne la ligne de l'aperçu Météo (causes, nuit noire) et mène au réglage. */
export function SmallDronesChip({ weather }: { weather: CoaWeather }) {
  const small = weather.small_drones;
  if (!small || small.status === "ok") return null;
  const line = smallDronesEffect(small);
  return (
    <Chip icon={Drone} onClick={openWeatherSetting} title={`${line} ${t("Cliquer pour régler la météo de l'exercice.")}`}>
      {small.status === "suspended" ? t("Petits drones interdits") : t("Petits drones limités")}
    </Chip>
  );
}

export function NightChip({ weather }: { weather: CoaWeather }) {
  if (!weather.dark) return null;
  return (
    <Chip icon={Moon} title={t("Nuit sombre le jour de l'heure H : les actions de nuit de l'ennemi sont plus probables dans le classement.")}>
      {darkNightText(weather.dark_reason ?? "moon", weather.moon_pct ?? 0)}
    </Chip>
  );
}

/** Marques d'un scénario modulé par la météo ou par la nuit sombre (tableau des scénarios en lice), annoncées au
 *  lecteur d'écran comme des images (l'icône seule est masquée). */
export function ContextMarks({ marks }: { marks: CoaMarks }) {
  const weather = marks.weather ?? 0;
  if (!weather && !marks.night) return null;
  return (
    <span className="flex shrink-0 items-center gap-0.5 text-ink-muted">
      {weather > 0 && (
        <span role="img" title={t("Favorisé par la météo")} aria-label={t("Favorisé par la météo")}>
          <ArrowUp size={12} />
        </span>
      )}
      {weather < 0 && (
        <span role="img" title={t("Défavorisé par la météo")} aria-label={t("Défavorisé par la météo")}>
          <ArrowDown size={12} />
        </span>
      )}
      {marks.night && (
        <span role="img" title={t("Favorisé par la nuit sombre")} aria-label={t("Favorisé par la nuit sombre")}>
          <Moon size={12} />
        </span>
      )}
    </span>
  );
}

function PhasePicker({ value, onChange }: { value: SketchPhase; onChange: (value: SketchPhase) => void }) {
  return (
    <Segmented
      size="sm"
      fill={false}
      ariaLabel={t("Temps du croquis")}
      className="shrink-0"
      value={value}
      onChange={onChange}
      options={PHASE_OPTIONS.map((o) => ({
        value: o.value,
        label: t(o.label),
        title: o.value === "all" ? t("Trajectoires de T1 à T3") : t("Zones des échelons ennemis en {phase}", { phase: o.value }),
      }))}
    />
  );
}

function RoleLegend({ roles, phase }: { roles: CoaRole[]; phase: SketchPhase }) {
  return (
    <ul className="divide-y divide-line border-b border-line">
      {roles.map((r) => (
        <li key={r.role} className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1 px-5 py-1.5">
          <svg width="22" height="12" className="shrink-0" aria-hidden>
            <rect x="1" y="1.5" width="20" height="9" rx="4.5" fill="var(--color-app6-hostile)" fillOpacity="0.12" stroke="var(--color-app6-hostile)" strokeWidth="1.3"
              strokeDasharray={ROLE_DASH[r.role]} />
          </svg>
          <span className="min-w-[160px] flex-1">
            <span className="block text-ui leading-tight font-semibold text-ink">{tServer(r.label)}</span>
            {r.units.length > 0 && (
              <span className="block truncate text-meta leading-tight text-ink-muted" title={r.units.map(citedUnit).join(", ")}>
                {r.units.map(citedUnit).join(", ")}
              </span>
            )}
          </span>
          <span className="ml-auto flex shrink-0 gap-1">
            {PHASES.map((ph) => {
              const time = r.times[ph];
              if (!time) return null;
              const current = phase === ph;
              return (
                <span
                  key={ph}
                  title={t("{phase} : {posture} (profondeur {from} à {to} % de l'axe)", {
                    phase: ph,
                    posture: tServer(time.posture).toLowerCase(),
                    from: Math.round(time.band[0] * 100),
                    to: Math.round(time.band[1] * 100),
                  })}
                  className={clsx(
                    "rounded-md px-1.5 py-0.5 text-caps leading-none font-medium whitespace-nowrap transition-colors",
                    current ? "bg-hostile-soft text-hostile" : phase === "all" ? "bg-canvas text-ink-soft" : "text-ink-muted",
                  )}
                >
                  <span className="font-semibold tabular-nums">{ph}</span> {tServer(time.posture)}
                </span>
              );
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MeCard({
  bloc,
  scenario,
  roles,
  data,
  phase,
  language,
  simulation,
}: {
  bloc: 1 | 2;
  scenario: CoaScenario | null;
  roles: CoaRole[];
  data: EnemyCoa;
  phase: SketchPhase;
  language: string;
  simulation: WargameSimulation | null;
}) {
  const forced = bloc === 1 ? data.forced.me1 : data.forced.me2;
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="border-b border-line px-5 pt-4 pb-3.5">
        <div className="flex items-center gap-2">
          <Badge tone="hostile" variant="solid">{t("ME{n}", { n: bloc })}</Badge>
          <SectionLabel inset>{bloc === 1 ? t("Le plus probable") : t("Le plus dangereux")}</SectionLabel>
          {scenario && (
            <Badge className="ml-auto" title={t("Orientation du scénario dans le catalogue")}>
              {t(ORIENTATION[scenario.orientation])}
            </Badge>
          )}
        </div>
        <h2 className="mt-2 text-title leading-snug font-semibold text-balance text-ink">
          {scenario ? scenario.name : t("Mode d'action {n}", { n: bloc })}
        </h2>
        {forced && scenario?.key === forced && (
          <p className="mt-1 text-meta text-ink-soft">{t("Retenu par l'animateur au WINGO de l'OPO précédent.")}</p>
        )}
      </header>
      {data.sketch && scenario ? (
        <>
          <div className="border-b border-line">
            <CoaSketch
              sketch={data.sketch}
              roles={roles}
              phase={phase}
              label={t("Croquis du ME{n} : {name}", { n: bloc, name: scenario.name })}
              contacts={simulation?.me === bloc && phase !== "all" ? simulation.phases[phase].contacts : undefined}
            />
            <SourcesNote text={data.sketch.attribution} />
          </div>
          <RoleLegend roles={roles} phase={phase} />
        </>
      ) : (
        <p className="border-b border-line px-5 py-6 text-center text-ui text-ink-muted">
          {t("Croquis indisponible : ni fuseau ni unités placées pour orienter l'axe.")}
        </p>
      )}
      {scenario ? (
        <p lang={language} className="px-5 pt-4 pb-5 text-justify text-title leading-relaxed text-ink hyphens-auto">
          {scenario.text}
        </p>
      ) : (
        <p className="px-5 py-5 text-ui text-ink-muted">{t("Aucun scénario du catalogue ne convient.")}</p>
      )}
    </section>
  );
}

function Meter({ label, value, hint }: { label: string; value: number | null; hint: string }) {
  const v = pct(value);
  return (
    <div title={hint}>
      <div className="flex items-baseline justify-between gap-3 text-ui">
        <span className="text-ink-soft">{label}</span>
        <span className="font-medium text-ink tabular-nums">{v === null ? "—" : t("{n} %", { n: v })}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-canvas ring-1 ring-line">
        <div className="h-full rounded-full bg-hostile/70 transition-[width] duration-(--gx-enter)" style={{ width: `${v ?? 0}%` }} />
      </div>
    </div>
  );
}

function ScoreCell({ value, retained, eligible }: { value: number | null; retained: boolean; eligible: boolean }) {
  const v = pct(value);
  return (
    <td className="px-3 py-1.5">
      <div className={clsx("flex items-center gap-2", !eligible && "opacity-40")}>
        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-canvas ring-1 ring-line">
          <div className={clsx("h-full rounded-full", retained ? "bg-hostile" : "bg-ink-muted/60")} style={{ width: `${v ?? 0}%` }} />
        </div>
        <span className="w-9 text-right text-meta text-ink-soft tabular-nums">{v === null ? "—" : t("{n} %", { n: v })}</span>
      </div>
    </td>
  );
}

function Trace({ data }: { data: EnemyCoa }) {
  const n = data.candidates.length;
  const posture =
    data.eni_posture === "defensive"
      ? t("l'ennemi est attendu en posture défensive")
      : data.eni_posture === "offensive"
        ? t("l'ennemi est attendu en posture offensive")
        : t("aucune posture ennemie n'est privilégiée");
  const sorted: CoaCandidate[] = [...data.candidates].sort(
    (a, b) => (b.score_me1 ?? 0) - (a.score_me1 ?? 0) || a.name.localeCompare(b.name),
  );
  const { context, disposition } = data;
  const terrain = TERRAIN[context.terrain];
  const weather = (context.weather_detail ?? null) as CoaWeather | null;
  const modulated = data.candidates.some((c) => Boolean(marksOf(c).weather || marksOf(c).night));
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="border-b border-line px-5 py-3">
        <h2 className="text-title font-semibold text-ink">{t("Pourquoi ces modes d'action")}</h2>
      </header>
      <div className="grid gap-6 px-5 py-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <SectionLabel as="h3" inset>{t("Posture ennemie")}</SectionLabel>
          <p className="text-ui leading-relaxed text-ink">
            {data.mode
              ? t("Mission amie « {mission} » ({mode}) : {posture}.", {
                  mission: missionName(data.mission),
                  mode: term(data.mode).toLowerCase(),
                  posture,
                })
              : t("Mission amie « {mission} » : {posture}.", { mission: missionName(data.mission), posture })}{" "}
            {t("{scenarios} sur {catalog} en lice", {
              // Ennemi générique (force non française, registre B39) : les scénarios, sans nom de type d'ennemi.
              scenarios: isNeutral(data.reference_enemy)
                ? plural(n, t("scénario"), t("scénarios"))
                : plural(n, t("scénario {type}", { type: data.eni_type }), t("scénarios {type}", { type: data.eni_type })),
              catalog: data.catalog,
            })}
            {data.posture_filtered ? "." : ` ${t("(aucun scénario de cette posture : tout le catalogue reste en lice).")}`}
          </p>
          <p className="text-meta leading-relaxed text-ink-muted">
            {t("Chaque scénario est noté à 45 % sur son biais doctrinal — le plus probable pour ME1, le plus dangereux pour ME2 —, ajusté au contexte, et à 55 % sur sa concordance avec la disposition de l'ennemi en T1. ME2 est choisi hors de ME1, dans une autre posture.")}
          </p>
          {data.enemy_profile && (
            <p className="text-ui leading-relaxed text-ink">{data.enemy_profile.text}</p>
          )}
        </div>
        <div className="space-y-2.5">
          <SectionLabel as="h3" inset>{t("Disposition ennemie en T1")}</SectionLabel>
          {disposition.valid ? (
            <>
              <Meter label={t("Échelonnement en profondeur")} value={disposition.depth}
                hint={t("Étalement des trois échelons ennemis le long de l'axe amis → ennemi")} />
              <Meter label={t("Débordement")} value={disposition.flank}
                hint={t("Écart du centre ennemi à l'axe du fuseau, rapporté à sa demi-largeur")} />
              <Meter label={t("Premier échelon au contact")} value={disposition.forward}
                hint={t("Avancée du premier échelon vers les amis")} />
            </>
          ) : (
            <p className="text-ui leading-relaxed text-ink-soft">
              {t("Moins de deux échelons ennemis placés en T1 : les scénarios sont départagés par les localités des échelons.")}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <SectionLabel as="h3" inset>{tc("modes d'action", "Contexte")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            <Chip title={t("Classe de terrain du lieu (nom du camp ou profil du terrain libre)")}>
              {t("Terrain {terrain}", { terrain: terrain ? t(terrain) : context.terrain })}
            </Chip>
            {weather && <WeatherChip weather={weather} />}
            {weather && <SmallDronesChip weather={weather} />}
            {weather && <NightChip weather={weather} />}
            {context.robotised && <Chip title={t("Bonus aux scénarios à unités robotisées")}>{t("Unités ennemies robotisées")}</Chip>}
            {context.sensitive_site && <Chip>{t("Site sensible")}</Chip>}
            {context.capops !== null && (
              <Chip title={t("Moyenne des CAPOPS saisis aux OPO précédents")}>{t("CAPOPS ami {n} %", { n: context.capops })}</Chip>
            )}
            {data.capops_filter && (
              <Chip>
                {data.capops_filter === "aggressive"
                  ? t("Ami usé : postures agressives seules")
                  : t("Ami renforcé : postures défensives seules")}
              </Chip>
            )}
            {data.enemy_profile && (
              <Chip title={t("Faiblesse à exploiter : {weakness}", { weakness: data.enemy_profile.weakness })}>
                {t("Chef ENI {profile}", { profile: data.enemy_profile.name.toLowerCase() })}
              </Chip>
            )}
            {data.forced.me1 && <Chip>{t("ME{n} imposé au WINGO", { n: 1 })}</Chip>}
            {data.forced.me2 && <Chip>{t("ME{n} imposé au WINGO", { n: 2 })}</Chip>}
          </div>
          {modulated && (
            <p className="text-meta leading-relaxed text-ink-muted">
              {t("Dans le tableau, les flèches signalent les scénarios favorisés ou défavorisés par la météo, la Lune ceux que la nuit sombre favorise.")}
            </p>
          )}
        </div>
      </div>
      <div className="overflow-x-auto border-t border-line">
        <table className="w-full min-w-[620px] text-left">
          <thead>
            <tr className="border-b border-line">
              <SectionLabel as="th" inset className="px-5 py-2">{t("Scénario en lice")}</SectionLabel>
              <SectionLabel as="th" inset className="px-3 py-2">{t("Orientation")}</SectionLabel>
              <SectionLabel as="th" inset className="px-3 py-2">{t("Le plus probable")}</SectionLabel>
              <SectionLabel as="th" inset className="px-3 py-2">{t("Le plus dangereux")}</SectionLabel>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sorted.map((c) => {
              const me1 = data.me1?.key === c.key;
              const me2 = data.me2?.key === c.key;
              return (
                <tr key={c.key} className={clsx(me1 || me2 ? "bg-hostile-soft/40" : undefined)}>
                  <td className="px-5 py-1.5">
                    <span className="flex items-center gap-2 text-ui text-ink">
                      {c.name}
                      <ContextMarks marks={marksOf(c)} />
                      {me1 && <Badge tone="hostile">{t("ME{n}", { n: 1 })}</Badge>}
                      {me2 && <Badge tone="hostile">{t("ME{n}", { n: 2 })}</Badge>}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-meta text-ink-soft">{t(ORIENTATION[c.orientation])}</td>
                  <ScoreCell value={c.score_me1} retained={me1} eligible={c.in_me1_pool} />
                  <ScoreCell value={c.score_me2} retained={me2} eligible={c.in_me2_pool} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function CoaView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const mapOpo = useUi((s) => s.opo);
  const setOpo = useUi((s) => s.setOpo);
  const mapPhase = useUi((s) => s.phase);
  const [phase, setPhase] = useState<SketchPhase>(mapPhase);
  const [wargameOpen, setWargameOpen] = useState(false);
  const [simulation, setSimulation] = useState<WargameSimulation | null>(null);
  const closeWargame = () => {
    setWargameOpen(false);
    setSimulation(null);
  };
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level && exercise.parameters.odb);
  const centac = exercise?.parameters.level === "CENTAC";
  const opo = effectiveOpo(params, mapOpo);
  const unsaved = Boolean(exerciseId && params && params.exercise_id !== exerciseId);
  const query = useQuery({
    queryKey: ["enemy-coa", exerciseId, centac ? opo : 1, exercise?.version],
    queryFn: () => api.enemyCoa(exerciseId as string, centac ? opo : undefined),
    enabled: Boolean(exerciseId && complete),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
  const data = query.data;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="coa"
        context={exercise?.name}
        badges={
          data &&
          (isNeutral(data.reference_enemy) ? (
            <Badge tone="hostile">{genericEnemyLabel()}</Badge>
          ) : (
            <Badge tone="hostile" title={t("Type d'ennemi du dossier")}>
              {data.eni_type}
            </Badge>
          ))
        }
        actions={
          <>
            {centac && <OpoSelector value={opo} onChange={setOpo} />}
            {data?.sketch && <PhasePicker value={phase} onChange={setPhase} />}
          </>
        }
        panelToggle={
          data && (
            <Button
              size="sm"
              variant={wargameOpen ? "secondary" : "primary"}
              onClick={() => (wargameOpen ? closeWargame() : setWargameOpen(true))}
              aria-pressed={wargameOpen}
              title={t("Confronter la manœuvre amie à un mode d'action ennemi")}
            >
              <Play size={14} /> {t("Simuler les ME")}
            </Button>
          )
        }
      />
      <Presence show={unsaved && Boolean(data)}>
        {unsaved && data && (
          <Motion origin="top">
            <SaveDossierHint variant="banner" action="appliquer vos dernières modifications aux modes d'action" />
          </Motion>
        )}
      </Presence>

      <div className="relative flex min-h-0 flex-1">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!exerciseId ? (
            <DossierEmptyState view="coa" kind="none">
              {t("Les modes d'action ennemis se déduisent du dossier : mission amie, type d'ennemi et dispositif sur la carte.")}
            </DossierEmptyState>
          ) : !complete ? (
            <DossierEmptyState view="coa" kind="incomplete">
              {t("Les modes d'action ennemis se déduisent du dossier : mission amie, type d'ennemi et dispositif sur la carte.")}
            </DossierEmptyState>
          ) : query.isError ? (
            zoneRefusalOf(query.error) ? (
              <ZoneWaitState message={query.error.message} error={query.error} />
            ) : (
              <EmptyState variant="error">{query.error.message}</EmptyState>
            )
          ) : !data ? (
            <EmptyState variant="loading" title={t("Sélection des modes d'action ennemis…")} />
          ) : (
            <div className="@container mx-auto w-full max-w-[1280px] space-y-6 px-6 py-6 max-sm:px-4">
              {/* Deux colonnes seulement si la place le permet (volet du wargame fermé ou grand écran). */}
              <div className="grid gap-6 @4xl:grid-cols-2">
                <MeCard bloc={1} scenario={data.me1} roles={data.sketch?.roles[0] ?? []} data={data} phase={phase}
                  language={exercise?.language ?? "fr"} simulation={simulation} />
                <MeCard bloc={2} scenario={data.me2} roles={data.sketch?.roles[1] ?? []} data={data} phase={phase}
                  language={exercise?.language ?? "fr"} simulation={simulation} />
              </div>
              <p className="text-meta leading-relaxed text-ink-muted">
                {t("Croquis schématique : chaque zone est la bande de profondeur que le scénario donne à l'échelon le long de l'axe du fuseau (des amis vers l'ennemi) et son étendue latérale ; les amis figurent en T1. Même texte que le paragraphe 1.a iv de l'OPO, qui peut ensuite être modifié dans l'ordre.")}
              </p>
              <Trace data={data} />
            </div>
          )}
        </div>
        {/* Wargame : volet latéral partagé, en surimpression avec voile sous 1280 px (les cartes ME gardent leur
            largeur sur tablette). */}
        <Presence show={wargameOpen && Boolean(data && exerciseId)}>
          {wargameOpen && data && exerciseId && (
            <WargamePanel
              key={`${exerciseId}-${data.opo}`}
              exerciseId={exerciseId}
              opo={centac ? opo : undefined}
              me1={data.me1}
              me2={data.me2}
              phase={phase}
              onPhase={setPhase}
              onSimulation={setSimulation}
              onClose={closeWargame}
            />
          )}
        </Presence>
      </div>
    </div>
  );
}
